import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ElementEvent } from 'library/ts/ui/events/ElementEvent';
import * as CSS from 'library/ts/utilities/modules/CSS';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import { getDeepVal } from 'library/ts/utilities/modules/Object';
import * as Type from 'library/ts/utilities/modules/Type';

/**
 * @description Replaces DOM element with new element
 * @param object {HTMLElement} current DOM object
 * @param string {String} replacement string
 */
export const replaceElement = ( object: HTMLElement, string: string ): void => {
	if ( object.outerHTML ) { // if outerHTML is supported
		object.outerHTML = string; // it's simple replacement of whole element with contents of str var
	} else { // if outerHTML is not supported, there is a weird but cross-browser trick
		const tmpObj = document.createElement( 'div' );
		tmpObj.innerHTML = '<!--placeholder-->';
		const ObjParent: any | HTMLElement = object.parentNode; // Ok, element should be parented
		ObjParent.replaceChild( tmpObj, object ); // here we are placing our temporary data instead of our target, so we can find it then and replace it into whatever we want to replace to
		ObjParent.innerHTML = ObjParent.innerHTML.replace( '<div><!--placeholder--></div>', string );
	}
};

/**
 * @description Declare namespace helper method. Creates nested namespaces
 * @param ns {String} Namespace string i.e. 'my.namespace'
 * @return {*} Returns ready to go namespace
 */
export const declareNamespace = ( ns: string ): any => {
	const nsParts: string[] = ns.split( '.' );
	const nsLength: number = nsParts.length;
	let root: Window = window;
	for ( let i = 0; i < nsLength; i++ ) {
		if ( typeof root[ nsParts[ i ] ] === TrueType.UNDEFINED ) root[ nsParts[ i ] ] = {};
		root = root[ nsParts[ i ] ];
	}
	return getDeepVal( window, ns ); // return namespace object
};

/**
 * @description Checks DOMParser seupport
 * @type {boolean}
 */
export const domParserSupport = ( () => {
	if ( !window.DOMParser ) return false;
	const parser = new DOMParser();
	try {
		parser.parseFromString( 'x', 'text/html' );
	} catch ( err ) {
		return false;
	}
	return true;
} )();

/**
 * @description Converts html string to dom element
 * @param {string} htmlString
 * @return {any}
 */
export const stringToElement = ( htmlString: string ): HTMLElement | HTMLDivElement | ChildNode | any => {
	// If DOMParser is supported, use it
	if ( domParserSupport ) {
		const parser = new DOMParser();
		const doc = parser.parseFromString( htmlString, 'text/html' );
		return doc.body.firstChild;
	}

	// Otherwise, fallback to old-school method
	const dom = document.createElement( 'div' );
	dom.innerHTML = htmlString;
	return dom;
};

/**
 * @description Returns rotation amount on Element
 * @param element {Element|Node}
 * @return {*|Number}
 */
export const getElementRotation = ( element: HTMLElement ): number => {
	const style = window.getComputedStyle( element, null );
	const transform = style.getPropertyValue( '-webkit-transform' ) ||
		style.getPropertyValue( '-moz-transform' ) ||
		style.getPropertyValue( '-ms-transform' ) ||
		style.getPropertyValue( '-o-transform' ) ||
		style.getPropertyValue( 'transform' ) ||
		'none';

	let angle = 0;

	if ( transform !== 'none' ) {
		// console.log( 'Matrix: ' + tr );
		let values: string = transform.split( '(' )[ 1 ];
		values = values.split( ')' )[ 0 ];
		const valuesSplit: string[] = values.split( ',' );
		const a = parseFloat( valuesSplit[ 0 ] );
		const b = parseFloat( valuesSplit[ 1 ] );
		const c = parseFloat( valuesSplit[ 2 ] );
		const d = parseFloat( valuesSplit[ 3 ] );

		const scale: any = Math.sqrt( a * a + b * b );

		// First option, don't check for negative result
		// Second, check for the negative result
		/**/
		const radians = Math.atan2( b, a );
		angle = Math.round( radians * ( 180 / Math.PI ) );
		/* /
		let radians = Math.atan2(b, a);
		if ( radians < 0 ) {
		  radians += (2 * Math.PI);
		}
		let angle = Math.round( radians * (180/Math.PI));
		/**/

	} else {
		angle = 0;
	}

	// works!
	// console.log( 'Rotate: ' + angle + 'deg' );
	return angle;
};

/**
 * @description Create array of elements from array of html strings
 * @param {string[]} elements
 * @return {HTMLElement[]}
 */
export const stringsArrayToElementsArray = ( elements: string[] = [] ): HTMLElement[] => {
	let out: HTMLElement[] | undefined = undefined;

	if ( Type.isArray( elements ) ) {
		if ( elements.length > 0 ) {
			out = [];
			for ( let i = 0; i < elements.length; i++ ) {
				out.push( stringToElement( elements[ i ] ) );
			}
		}
	}

	return out;
};

/**
 * @description Appends elements stored in array to a supplied parent
 * @param {HTMLElement | unknown } parent
 * @param {HTMLElement[]} elements
 */
export const appendElementsArray = ( parent: HTMLElement | unknown = window, elements: HTMLElement[] = [] ): void => {
	if ( Type.isUndefined( elements ) ) return;
	if ( Type.isArray( elements ) === false ) return;
	for ( let i = 0; i < elements.length; i++ ) {
		if ( Type.isElement( elements[ i ] ) ) {
			( parent as HTMLElement ).appendChild( elements[ i ] );
		}
	}
};

/**
 * @description Removes all children from element
 * @param element {HTMLElement}
 * @param destroy {boolean} - optional flag to request setting removed element undefined to free memory
 */
export const emptyElement = ( element: HTMLElement, destroy: boolean = true ): void => {
	if ( !Type.isElement( element ) ) return; // sanity check
	while ( element.firstChild ) {
		let removedNode: ChildNode = element.removeChild( element.firstChild );
		if ( destroy === true ) removedNode = undefined;
	}
};

/**
 * @description Cleans element node from white spaces
 * @param node {Object}
 */
export const cleanElementNode = ( node: Element | ChildNode ): void => {
	for ( let n = 0; n < node.childNodes.length; n++ ) {
		const child = node.childNodes[ n ];
		if (
			child.nodeType === 8
			||
			( child.nodeType === 3 && !/\S/.test( child.nodeValue ) )
		) {
			node.removeChild( child );
			n--;
		} else if (
			child.nodeType === 1
		) {
			cleanElementNode( child );
		}
	}
};

/**
 * @description Gets elements by attribute
 * @param attribute {String}
 * @param context {Element|Object}
 * @return {Array}
 */
export const getElementsByAttribute = ( attribute: string, context: Element ): HTMLElement[] => {
	const nodeList = ( context || document ).getElementsByTagName( '*' );
	const nodeArray: HTMLElement[] = [];
	let iterator = 0;
	/**
	 * @type {null|HTMLElement}>}
	 */
	const node = null;

	while ( node === nodeList[ iterator++ ] ) {
		if ( node.getAttribute( attribute ) ) nodeArray.push( node );
	}

	return nodeArray;
};

/**
 * @description Returns elements by partial attribute value
 * @param parentElement {HTMLElement} default - document
 * @param htmlTag {String} tag name, default 'html'
 * @param attributeName {String} attribute name, default 'id'
 * @param value {String} partial value of element attribute
 * @return {NodeListOf<Element>}
 */
export const getElementsByPartialAttribute = (
	parentElement: HTMLElement | Document = document,
	htmlTag: string = 'div',
	attributeName: string = 'id',
	value: string = ''
): NodeListOf<Element> => {
	const query = htmlTag + '[' + attributeName + '^="' + value + '"]';
	return parentElement.querySelectorAll( query );
};

/**
 * @description Removes elements by partial id
 * @param tag {String} tag name
 * @param part_id {String} partial id of element
 */
export const removeElementByPartialID = ( tag: string, part_id: string ): void => {
	const query: string = tag + '[id^="' + part_id + '"]';
	const elements: NodeListOf<Element> = document.querySelectorAll( query );
	const forEach = Array.prototype.forEach;

	forEach.call(
		elements,
		( d ) => {
			d.parentNode.removeChild( d );
		}
	);
};

/**
 * @description Removes DOM element
 * @param {HTMLElement} element
 * @returns {Promise<IPromiseResolveGeneric & {element?: HTMLElement, parent?: HTMLElement}>}
 */
export const removeElement = ( element: HTMLElement ): Promise<IPromiseResolveGeneric & { element?: HTMLElement, parent?: HTMLElement }> => {
	return new Promise(
		( resolve, reject ) => {
			switch ( Type.isElement( element ) && Type.isElement( element?.parentNode ) ) {
				case true:
					element.parentNode.removeChild( element );
					resolve( { msg: 'Element removed', success: true } );
					break;
				case false:
					resolve( { msg: 'Element removal failure', success: false, result: { element: element, parent: element?.parentNode } } );
					break;
			}
			resolve( { msg: 'Element removed', success: true } );
		}
	);
};

/**
 * @description Selects list element option by value and returns index of selected element
 * @param list {HTMLSelectElement}
 * @param value {String}
 * @return {Number}
 */
export const selectListItemByValue = ( list: HTMLSelectElement, value: string ): number => {
	for ( let i = 0; i < list.options.length; i++ ) {
		if ( list.options[ i ].value === value ) {
			list.selectedIndex = i;
			break;
		}
	}
	return list.selectedIndex; // return selected index
};

/**
 * @description Insert a new element after an existing one in the DOM tree
 * @param {HTMLElement | Element} element
 * @param {HTMLElement | Element | Node} referenceNode
 */
export const insertAfter = ( element: HTMLElement | Element, referenceNode: HTMLElement | Element | Node ): void => {
	referenceNode.parentNode.insertBefore( element, referenceNode.nextSibling );
};

/**
 * @description Insert an element before another one
 * @param {HTMLElement | Element} element
 * @param {HTMLElement | Element | Node} referenceNode
 */
export const insertBefore = ( element: HTMLElement | Element, referenceNode: HTMLElement | Element | Node ): void => {
	referenceNode.parentNode.insertBefore( element, referenceNode );
};

/**
 * @description Recreates HTMLElement node removing all event listeners as a side effect
 * @param {HTMLElement} element
 * @param {boolean} withChildren
 */
export const recreateNode = ( element: HTMLElement, withChildren: boolean = false ): Promise<IPromiseResolveGeneric> => {
	return new Promise(
		( resolve, _reject ) => {
			if ( withChildren ) {
				element?.parentNode?.replaceChild( element?.cloneNode( true ), element );
			} else {
				const newElement = element?.cloneNode( false );
				while ( element?.hasChildNodes() ) {
					newElement?.appendChild( element?.firstChild );
				}
				element?.parentNode?.replaceChild( newElement, element );
			}
			resolve( { msg: 'Element replaced', success: true } );
		}
	);
};

/**
 * @description Returns list of dom events for an element
 * @param {HTMLElement | Element} element
 * @returns {string[]}
 */
export const domEvents = ( element: HTMLElement | Element = document.body ): string[] => {
	const eventList: string[] = [];
	for ( const key in element ) {
		if ( /\bon/.test( key ) ) {
			eventList.push( key );
		}
	}
	return eventList;
};

/**
 * @description Show/Hide element visually and from screen readers
 * @param {HTMLElement} element HTMLElement to modify
 * @param {boolean} isVisible Should element be visible to users and screen readers
 * @param {string} addClass CSS classes to add to element
 * @param {string} removeClass CSS classes to remove from element
 */
export const toggleElementVisibility = ( element: HTMLElement, isVisible: boolean, addClass?: string, removeClass?: string ): void => {
	if ( Type.isElement( element ) ) {
		if ( addClass ) CSS.addClass( element, addClass );
		if ( removeClass ) CSS.removeClass( element, removeClass );
		// Blur any focused descendant before marking the subtree aria-hidden.
		// Browsers block aria-hidden on an element whose descendant retains
		// focus (per W3C ARIA spec), which would leave the subtree announced
		// to screen readers despite being visually hidden. Dropping focus to
		// body lets the attribute apply and converges to the expected state
		// when content is being torn down.
		if (
			!isVisible
			&&
			element.contains( document.activeElement )
			&&
			document.activeElement !== document.body
		) {
			( document.activeElement as HTMLElement ).blur();
		}
		element.setAttribute( 'aria-hidden', isVisible ? 'false' : 'true' );
	}
};

/**
 * @description Returns composed path for DOM element
 * @param {HTMLElement | Element} domElement
 * @returns {EventTarget[]}
 */
export const getComposedPath = ( domElement: HTMLElement | Element ): EventTarget[] => {
	const path: EventTarget[] = [];
	let currentElement = domElement;
	while ( currentElement ) {
		path.push( currentElement );
		currentElement = currentElement.parentElement;
	}
	if ( path.indexOf( window ) === -1 && path.indexOf( document ) === -1 )
		path.push( document );
	if ( path.indexOf( window ) === -1 )
		path.push( window );
	return path;
};

/**
 * @description Tests is element contains any of VTG CSS class patterns
 * @param {HTMLElement | Element} element
 * @returns {boolean}
 */
export const isVTGElement = ( element: HTMLElement | Element ): boolean => {
	if ( Type.isElement( element ) ) {
		if (
			!Type.isEmpty( element?.className )
			&&
			element.className.split( ' ' ).some( ( cssClass ) => /vtgd-.*/.test( cssClass ) )
		) {
			return true; // element owned by VTG
		}
	}
	return false;
};

/**
 * @description Check is DOM was fully loaded and executes callback function
 * @param callbackFunction
 */
export const onDocumentReady = ( callbackFunction ) => {
	// if callback is not a function => exit
	if ( !Type.isFunction( callbackFunction ) ) return;

	if ( document.readyState !== 'loading' ) {
		// Document is already ready, call the callback directly
		callbackFunction();
	} else if ( document.addEventListener ) {
		// All modern browsers to register DOMContentLoaded
		document.addEventListener( 'DOMContentLoaded', callbackFunction );
	} else {
		// Old IE browsers
		document[ 'attachEvent' ](
			'onreadystatechange',
			function () {
				if ( document.readyState === 'complete' ) {
					callbackFunction();
				}
			}
		);
	}
};

/**
 * @description Returns HTMLElement per passed in argument
 * @param obj - string, JQuery object, HTMLElement
 * @returns {HTMLElement | undefined}
 */
export const getElementAdvanced = ( obj: any | HTMLElement ): HTMLElement | undefined => {
	let element: HTMLElement;

	if ( Type.isEmpty( obj ) ) {
		element = undefined;
	}
	if ( Type.isString( obj ) ) {
		element = document.querySelector( obj as string );
	}

	if ( Type.isJQueryObject( obj ) ) {
		element = obj[ 0 ];
	}
	if ( Type.isElement( obj ) ) {
		element = obj;
	}
	if ( Type.isElement( element ) ) {
		return element;
	}
	return undefined;
};

/**
 * @description Disables picture-in-picture on video element
 * @param {HTMLVideoElement} videoElement
 */
export const disablePictureInPicture = ( videoElement: HTMLVideoElement ): void => {
	if ( Type.isElement( videoElement ) && videoElement?.tagName?.toUpperCase() === 'VIDEO' ) {
		videoElement.setAttribute( 'disablepictureinpicture', '' );
	}
};

/**
 * @descriptionDisable native controls and optionally suppress right-click on a video element.
 * @param video - HTMLVideoElement to apply settings to
 * @param suppressContextMenu - if true, disables right-click on the video
 */
export function setupCustomVideoControls(
	video: HTMLVideoElement,
	suppressContextMenu: boolean = true
): ( () => void ) | void {
	const DATA_CUSTOM_CONTROLS_INITIALIZED = 'data-custom-controls-initialized';

	// Safeguard 1: Check if video exists
	if ( !video ) {
		console.warn( 'No video element provided' );
		return;
	}

	// Safeguard 2: Verify it's actually a video element
	if ( !( video instanceof HTMLVideoElement ) ) {
		console.warn( 'Provided element is not an HTMLVideoElement' );
		return;
	}

	// Safeguard 3: Check if already initialized to prevent duplicate listeners
	if ( video.hasAttribute( DATA_CUSTOM_CONTROLS_INITIALIZED ) ) {
		console.warn( 'Custom controls already initialized on this video element' );
		return;
	}

	try {
		// Disable native controls
		video.controls = false;

		// Disable fullscreen/download/remote buttons for supported browsers
		video.setAttribute( 'controlsList', 'nofullscreen nodownload noremoteplayback' );

		// Mark as initialized
		video.setAttribute( DATA_CUSTOM_CONTROLS_INITIALIZED, 'true' );

		// Suppress right-click if requested
		let contextMenuHandler: ( ( event: MouseEvent ) => void ) | null = null;

		if ( suppressContextMenu ) {
			contextMenuHandler = ( event: MouseEvent ) => {
				event.preventDefault();
				console.log( 'Right-click suppressed on video' );
				// Optional: show your own custom context menu here
			};
			video.addEventListener( ElementEvent.MOUSE_CONTEXT_MENU, contextMenuHandler );
		}

		console.log( 'Video element initialized with custom controls' );

		// Safeguard 4: Return cleanup function for proper resource management
		return () => {
			if ( contextMenuHandler ) {
				video.removeEventListener( ElementEvent.MOUSE_CONTEXT_MENU, contextMenuHandler );
			}
			video.removeAttribute( DATA_CUSTOM_CONTROLS_INITIALIZED );
			console.log( 'Custom video controls cleaned up' );
		};
	} catch ( error ) {
		// Safeguard 5: Error handling for any unexpected issues
		console.warn( 'Error setting up custom video controls:', error );
		return;
	}
}